/**
 * STAGING ONLY — diagnostic mutation/read probes for Classic Site verification.
 *
 * Do NOT import from member-facing pages or production CRUD services.
 * All functions require explicit caller invocation.
 *
 * Auth: credentials:include via hoodieFetchSession
 * CSRF: X-CSRFToken on mutating requests when csrf_token cookie present
 */

import {
  getCsrfToken,
  hoodieFetchSession,
  hoodieFetchSessionJson,
} from "../../../api/hoodieClient";
import { HOODIE_API_BASE_URL } from "../../../api/config";

/**
 * @typedef {Object} StagingProbeResult
 * @property {boolean} ok
 * @property {number} status
 * @property {string} method
 * @property {string} path
 * @property {string} fullUrl
 * @property {unknown} data
 * @property {string|null} errorMessage
 * @property {boolean} credentialsIncluded
 * @property {boolean} csrfCookiePresent
 * @property {boolean} csrfHeaderSent
 * @property {string} timestamp
 */

function buildResult(method, path, response, data, ok) {
  const csrfCookiePresent = Boolean(getCsrfToken());
  const fullUrl = path.startsWith("http") ? path : HOODIE_API_BASE_URL + path;
  return {
    ok,
    status: response.status,
    method: method.toUpperCase(),
    path,
    fullUrl,
    data,
    errorMessage: ok
      ? null
      : data?.detail || data?.message || `HTTP ${response.status}`,
    credentialsIncluded: true,
    csrfCookiePresent,
    csrfHeaderSent:
      !/^(GET|HEAD|OPTIONS|TRACE)$/i.test(method) && csrfCookiePresent,
    timestamp: new Date().toISOString(),
  };
}

async function parseResponseJson(response) {
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }
  return null;
}

/** @returns {Promise<StagingProbeResult>} */
export async function stagingProbeGet(path) {
  const { data, response, ok } = await hoodieFetchSessionJson(path);
  return buildResult("GET", path, response, data, ok);
}

/** @returns {Promise<StagingProbeResult>} */
export async function stagingProbePatchJson(path, patchObject) {
  const { data, response, ok } = await hoodieFetchSessionJson(path, {
    method: "PATCH",
    body: JSON.stringify(patchObject || {}),
  });
  return buildResult("PATCH", path, response, data, ok);
}

/** @returns {Promise<StagingProbeResult>} */
export async function stagingProbePostJson(path, bodyObject) {
  const { data, response, ok } = await hoodieFetchSessionJson(path, {
    method: "POST",
    body: JSON.stringify(bodyObject || {}),
  });
  return buildResult("POST", path, response, data, ok);
}

/** @returns {Promise<StagingProbeResult>} */
export async function stagingProbeDelete(path) {
  const { data, response, ok } = await hoodieFetchSessionJson(path, {
    method: "DELETE",
  });
  return buildResult("DELETE", path, response, data, ok);
}

/**
 * PATCH multipart (e.g. graphic upload test).
 * @param {string} path
 * @param {FormData} formData
 * @returns {Promise<StagingProbeResult>}
 */
export async function stagingProbePatchMultipart(path, formData) {
  const csrf = getCsrfToken();
  const headers = new Headers();
  headers.set("Accept", "application/json");
  if (csrf) headers.set("X-CSRFToken", csrf);

  const response = await hoodieFetchSession(path, {
    method: "PATCH",
    headers,
    body: formData,
  });
  const data = await parseResponseJson(response);
  return buildResult("PATCH", path, response, data, response.ok);
}

/**
 * No-op PATCH: GET listing first, re-PATCH same business_name.
 * @param {string|number} listingId
 */
export async function stagingProbeListingNoOpPatch(listingId) {
  const id = String(listingId).trim();
  const getResult = await stagingProbeGet(`listings/${id}/`);
  if (!getResult.ok || !getResult.data?.business_name) {
    return {
      ...getResult,
      errorMessage:
        getResult.errorMessage ||
        "Cannot no-op PATCH: GET listing failed or missing business_name",
    };
  }
  return stagingProbePatchJson(`listings/${id}/`, {
    business_name: getResult.data.business_name,
  });
}

/**
 * No-op PATCH: GET event first, re-PATCH same name.
 * @param {string|number} eventId
 */
export async function stagingProbeEventNoOpPatch(eventId) {
  const id = String(eventId).trim();
  const getResult = await stagingProbeGet(`events/${id}/`);
  if (!getResult.ok || !getResult.data?.name) {
    return {
      ...getResult,
      errorMessage:
        getResult.errorMessage ||
        "Cannot no-op PATCH: GET event failed or missing name",
    };
  }
  return stagingProbePatchJson(`events/${id}/`, {
    name: getResult.data.name,
  });
}

/**
 * No-op PATCH: GET promo first, re-PATCH same name.
 * @param {string|number} promoId
 */
export async function stagingProbePromoNoOpPatch(promoId) {
  const id = String(promoId).trim();
  const getResult = await stagingProbeGet(`promos/${id}/`);
  if (!getResult.ok || !getResult.data?.name) {
    return {
      ...getResult,
      errorMessage:
        getResult.errorMessage ||
        "Cannot no-op PATCH: GET promo failed or missing name",
    };
  }
  return stagingProbePatchJson(`promos/${id}/`, {
    name: getResult.data.name,
  });
}

export { HOODIE_API_BASE_URL };
